import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "../lib/api";
import { useApiBase } from "./ApiBaseContext";

const CaptureContext = createContext(null);

export function CaptureProvider({ projectId, children }) {
    const apiBase = useApiBase();
    const [unassignedCount, setUnassignedCount] = useState(0);
    const [actionRequiredCount, setActionRequiredCount] = useState(0);
    // Owners get a separate pending-request count so the Project Settings
    // page can highlight the section without conflating it with HOD flags.
    const [pendingPermissionCount, setPendingPermissionCount] = useState(0);

    const refreshCount = useCallback(async () => {
        if (!projectId) return;
        // Iter-158.13.2.20e.2 — Production T&A page uses
        // apiBase="/beta/projects" but the H&M-scope counters
        // (unassigned state entries, flags pending, permission
        // requests) are only mounted at the H&M or legacy prefix.
        // Skip when we're on the Production scope so the settings
        // page doesn't spam 404s.
        if (apiBase === "/beta/projects") return;
        try {
            const [u, f, p] = await Promise.all([
                api.get(`${apiBase}/${projectId}/state-entries/unassigned-count`),
                api.get(`${apiBase}/${projectId}/flags/pending-count`, {
                    params: { mine: true },
                }),
                api.get(`${apiBase}/${projectId}/permission-requests/pending-count`),
            ]);
            const uc = Number(u?.data?.count) || 0;
            const fc = Number(f?.data?.count) || 0;
            const pc = Number(p?.data?.count) || 0;
            setUnassignedCount(uc);
            // The Action Required badge bundles two owner-relevant
            // signals: HOD flags addressed to me + pending permission
            // requests on projects I own. Members only ever see flags.
            setActionRequiredCount(fc + pc);
            setPendingPermissionCount(pc);
        } catch {
            // ignore
        }
    }, [projectId, apiBase]);

    useEffect(() => {
        refreshCount();
    }, [refreshCount]);

    const value = {
        projectId,
        unassignedCount,
        actionRequiredCount,
        pendingPermissionCount,
        refreshCount,
    };
    return (
        <CaptureContext.Provider value={value}>{children}</CaptureContext.Provider>
    );
}

export function useCaptureContext() {
    return useContext(CaptureContext);
}
